import { createSlice, isAnyOf, isPending } from '@reduxjs/toolkit';
import {
  ThunkGetAzureResourceData,
  ThunkValidateDomain,
  ThunkAssignDomain,
  ThunkGetCustomDomain,
  ThunkDeleteCustomDomain,
  ThunkBindSslCertificate,
  ThunkCreateSslCertificate,
} from '@store/slices/wildcardDomains/thunks';
import { WILDCARD_DOMAINS } from '@store/slices/resources';

const initialState = {
  resourceInfo: {},
  resourceInfoLoading: false,
  validationResult: null,
  validationInProgress: false,
  assigningDomainResult: null,
  assigningDomainsInProgress: false,
  customDomain: null,
  deletingIsInProgress: false,
  bindCertificateInProgress: false,
  createCertificateInProgress: false,
};

const wildcardDomainsSlice = createSlice({
  name: WILDCARD_DOMAINS,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(ThunkGetAzureResourceData.fulfilled, (state, { payload }) => {
        state.resourceInfo = payload;
        state.resourceInfoLoading = false;
      })
      .addCase(ThunkValidateDomain.fulfilled, (state, { payload }) => {
        state.validationResult = payload;
        state.validationInProgress = false;
      })
      .addCase(ThunkAssignDomain.fulfilled, (state, { payload }) => {
        state.assigningDomainResult = payload;
        state.assigningDomainsInProgress = false;
      })
      .addCase(ThunkGetCustomDomain.fulfilled, (state, { payload }) => {
        state.customDomain = payload;
        state.resourceInfoLoading = false;
      })
      .addCase(ThunkDeleteCustomDomain.fulfilled, state => {
        state.deletingIsInProgress = false;
        state.customDomain = { domain: null };
      })
      .addCase(ThunkBindSslCertificate.fulfilled, (state, { payload }) => {
        state.bindCertificateInProgress = false;
        state.customDomain = payload;
      })
      .addCase(ThunkCreateSslCertificate.fulfilled, (state, { payload }) => {
        state.createCertificateInProgress = false;
        state.customDomain = payload;
      })

      .addCase(ThunkValidateDomain.rejected, state => {
        state.validationResult = null;
        state.validationInProgress = false;
      })
      .addCase(ThunkAssignDomain.rejected, state => {
        state.assigningDomainResult = null;
        state.assigningDomainsInProgress = false;
      })
      .addCase(ThunkGetCustomDomain.rejected, state => {
        state.resourceInfoLoading = false;
      })
      .addCase(ThunkDeleteCustomDomain.rejected, state => {
        state.deletingIsInProgress = false;
      })
      .addCase(ThunkBindSslCertificate.rejected, state => {
        state.bindCertificateInProgress = false;
      })
      .addCase(ThunkCreateSslCertificate.rejected, state => {
        state.createCertificateInProgress = false;
      })

      .addMatcher(isAnyOf(isPending(ThunkGetCustomDomain)), state => {
        state.resourceInfoLoading = true;
      })
      .addMatcher(isAnyOf(isPending(ThunkGetAzureResourceData)), state => {
        state.resourceInfoLoading = true;
      })
      .addMatcher(isAnyOf(isPending(ThunkValidateDomain)), state => {
        state.validationInProgress = true;
      })
      .addMatcher(isAnyOf(isPending(ThunkAssignDomain)), state => {
        state.assigningDomainsInProgress = true;
      })
      .addMatcher(isAnyOf(isPending(ThunkDeleteCustomDomain)), state => {
        state.deletingIsInProgress = true;
      })
      .addMatcher(isAnyOf(isPending(ThunkBindSslCertificate)), state => {
        state.bindCertificateInProgress = true;
      })
      .addMatcher(isAnyOf(isPending(ThunkCreateSslCertificate)), state => {
        state.createCertificateInProgress = true;
      });
  },
});

export default wildcardDomainsSlice.reducer;

export const WildCardDomainsResourceInfoSelector = state =>
  state[WILDCARD_DOMAINS].resourceInfo;

export const WildCardDomainsResourceInfoLoadingSelector = state =>
  state[WILDCARD_DOMAINS].resourceInfoLoading;

export const WildCardDomainsValidationResultSelector = state =>
  state[WILDCARD_DOMAINS].validationResult;

export const WildCardDomainsValidationResultLoadingSelector = state =>
  state[WILDCARD_DOMAINS].validationInProgress;

export const WildCardDomainsAssignDomainResult = state =>
  state[WILDCARD_DOMAINS].assigningDomainResult;

export const WildCardDomainsAssignDomainLoading = state =>
  state[WILDCARD_DOMAINS].assigningDomainsInProgress;

export const WildCardDomainsCustomDomain = state =>
  state[WILDCARD_DOMAINS].customDomain;

export const WildCardDomainsDeleteDomain = state =>
  state[WILDCARD_DOMAINS].deletingIsInProgress;

export const WildCardDomainsCertGenerateLoading = state =>
  state[WILDCARD_DOMAINS].createCertificateInProgress;

export const WildCardDomainsBindSslCertificate = state =>
  state[WILDCARD_DOMAINS].bindCertificateInProgress;
